import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import FindPastClientsWizard from './FindPastClientsWizard';
import { parseImportFile } from '../../../utils/import-file-parser';

const ImportWizard = ({ isOpen, onClose, onImport }) => {
  const [mode, setMode] = useState(null); // null | 'pastClients' | 'classic'
  const [currentStep, setCurrentStep] = useState(1);
  const [importData, setImportData] = useState({
    file: null,
    csvHeaders: [],
    csvData: [],
    mappings: {},
    options: {
      skipFirstRow: true,
      updateExisting: false
    }
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [parseError, setParseError] = useState(null);

  const resetState = () => {
    setMode(null);
    setCurrentStep(1);
    setImportData({
      file: null,
      csvHeaders: [],
      csvData: [],
      mappings: {},
      options: { skipFirstRow: true, updateExisting: false }
    });
    setParseError(null);
    setIsProcessing(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const systemFields = [
    { value: '', label: 'Skip this column' },
    { value: 'name', label: 'Full Name (Required)' },
    { value: 'firstName', label: 'First Name' },
    { value: 'lastName', label: 'Last Name' },
    { value: 'email', label: 'Email Address (Optional)' },
    { value: 'phone', label: 'Phone Number' },
    { value: 'location', label: 'Address (Optional)' },
    { value: 'address', label: 'Street Address (Optional, combined into Address)' },
    { value: 'city', label: 'City (Optional, combined into Address)' },
    { value: 'state', label: 'State (Optional, combined into Address)' },
    { value: 'zipCode', label: 'Zip Code (Optional, combined into Address)' },
    { value: 'lifecycleStage', label: 'Lifecycle Stage (Lead, Active Buyer, etc.)' },
    { value: 'engagementLevel', label: 'Engagement Level (High, Medium, Low)' },
    { value: 'tags', label: 'Tags (comma separated)' },
    { value: 'notes', label: 'Notes' },
    { value: 'birthday', label: 'Birthday' },
    { value: 'homeClosingDate', label: 'Home Closing Date' }
  ];

  const smartMatchColumn = (csvHeader) => {
    if (!csvHeader) return '';

    const normalized = csvHeader.toLowerCase().replace(/[_\s-.,()]/g, '');
    const tokens = csvHeader.toLowerCase().split(/[\s_\-.,()]+/).filter(Boolean);
    const hasToken = (t) => tokens.includes(t);
    const hasAllTokens = (...ts) => ts.every((t) => hasToken(t));

    // 1. Exact normalized header match (most specific keywords first)
    const exactPatterns = [
      ['lastName', ['lastname', 'lname', 'surname', 'familyname']],
      ['firstName', ['firstname', 'fname', 'givenname']],
      ['homeClosingDate', ['closingdate', 'closedate', 'homeclosing', 'homeanniversary', 'homeanniv', 'homeannivp', 'homeannivl', 'purchasedate', 'anniversarydate']],
      ['birthday', ['birthday', 'dateofbirth', 'dob', 'birthdate', 'bday']],
      ['lifecycleStage', ['lifecyclestage', 'leadstage', 'dealstage', 'clientstatus', 'leadstatus', 'pipelinestage', 'lifecycle', 'stage', 'status']],
      ['engagementLevel', ['engagementlevel']],
      ['email', ['email', 'emailaddress', 'emailid', 'email1']],
      ['phone', ['phone', 'phonenumber', 'mobile', 'cell', 'telephone', 'cellphone', 'cellphone1', 'phone1']],
      ['location', ['location', 'mailingaddress', 'fulladdress', 'homeaddress', 'propertyaddress', 'clientaddress']],
      ['address', ['streetaddress', 'mailingstreet', 'address1', 'address']],
      ['zipCode', ['zipcode', 'postalcode', 'postcode', 'mailingzip', 'zip']],
      ['city', ['city', 'town', 'mailingcity']],
      ['state', ['state', 'province', 'region', 'mailingstate']],
      ['tags', ['tags', 'categories']],
      ['notes', ['notes', 'comments', 'comment', 'description', 'memo', 'remarks']],
      ['name', ['fullname', 'name', 'clientname', 'contactname', 'customername', 'client', 'customer', 'lead']],
    ];

    for (const [field, keywords] of exactPatterns) {
      if (keywords.includes(normalized)) return field;
    }

    // 2. Token-based matches (avoids substring false positives like "last" in "Days Since Last Stage...")
    if (hasAllTokens('last', 'name') || hasToken('lname') || hasToken('surname')) return 'lastName';
    if (hasAllTokens('first', 'name') || hasToken('fname') || hasToken('givenname')) return 'firstName';
    if (hasToken('email')) return 'email';
    if (hasToken('phone') || hasToken('mobile') || hasToken('cell') || hasToken('telephone')) return 'phone';
    if (hasToken('birthday') || hasToken('dob')) return 'birthday';
    if (hasToken('zip') || hasToken('postal') || hasToken('postcode')) return 'zipCode';
    if (hasToken('city') || hasToken('town')) return 'city';
    if (hasToken('state') || hasToken('province')) return 'state';
    if (hasToken('address') && !hasToken('email')) return 'address';
    if (hasToken('street')) return 'address';
    if (hasToken('tags') || hasToken('tag')) return 'tags';
    if (hasToken('notes') || hasToken('note')) return 'notes';
    if (hasToken('engagement') || hasToken('priority') || hasToken('rating')) return 'engagementLevel';
    if (hasToken('closing') && hasToken('date')) return 'homeClosingDate';
    // Home anniversary columns (e.g. "Home Anniv - P") → closing / home anniversary date
    if (
      (hasToken('anniv') || hasToken('anniversary')) &&
      (hasToken('home') || hasToken('house') || hasToken('purchase'))
    ) {
      return 'homeClosingDate';
    }
    if (hasToken('status') && !hasToken('homeowner')) return 'lifecycleStage';

    // "Name" alone, not part of first/last/user/company/stage column names
    if (
      hasToken('name') &&
      !hasToken('first') &&
      !hasToken('last') &&
      !hasToken('user') &&
      !hasToken('company') &&
      !hasToken('stage')
    ) {
      return 'name';
    }

    if (hasToken('client') || hasToken('customer')) return 'name';
    if (hasToken('location') || hasToken('neighborhood') || hasToken('territory')) return 'location';

    // 3. Long substring matches only (>= 6 chars to avoid "last", "stage", "name", etc.)
    const longPartialPatterns = {
      lifecycleStage: ['lifecycle', 'pipeline', 'leadstage', 'dealstage'],
      engagementLevel: ['engagement'],
      homeClosingDate: ['closingdate', 'purchasedate', 'homeclosing'],
      location: ['mailingaddress', 'fulladdress', 'homeaddress'],
      address: ['streetaddress', 'mailingstreet'],
      email: ['emailaddress'],
      phone: ['phonenumber'],
    };

    for (const [field, keywords] of Object.entries(longPartialPatterns)) {
      for (const keyword of keywords) {
        if (keyword.length >= 6 && normalized.includes(keyword)) return field;
      }
    }

    return '';
  };

  const autoMapFields = (headers) => {
    const mappings = {};
    const usedFields = new Set();
    
    headers.forEach(header => {
      const match = smartMatchColumn(header);
      if (match && !usedFields.has(match)) {
        mappings[header] = match;
        usedFields.add(match);
      }
    });
    
    return mappings;
  };

  const parseCSV = (text) => {
    // Kept for compatibility; prefer parseImportFile for uploads
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      throw new Error('CSV file is empty');
    }

    const parseCSVLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseCSVLine(lines[0]);
    const data = lines.slice(1).map(line => parseCSVLine(line));

    return { headers, data };
  };

  const handleFileUpload = async (e) => {
    const file = e?.target?.files?.[0];
    setParseError(null);

    if (!file) return;

    try {
      const parsed = await parseImportFile(file);

      if (parsed.headers.length === 0) {
        throw new Error('No headers found in file');
      }

      const autoMappings = autoMapFields(parsed.headers);

      setImportData(prev => ({
        ...prev,
        file,
        csvHeaders: parsed.headers,
        csvData: parsed.rows,
        mappings: autoMappings
      }));
    } catch (error) {
      console.error('Error parsing file:', error);
      // Fallback to legacy parser for odd CSVs
      try {
        if (!file.name.toLowerCase().endsWith('.csv')) throw error;
        const text = await file.text();
        const { headers, data } = parseCSV(text);
        const autoMappings = autoMapFields(headers);
        setImportData(prev => ({
          ...prev,
          file,
          csvHeaders: headers,
          csvData: data,
          mappings: autoMappings
        }));
      } catch (fallbackErr) {
        setParseError(fallbackErr.message || error.message || 'Failed to parse file.');
      }
    }
  };

  const handleMappingChange = (csvField, systemField) => {
    setImportData(prev => ({
      ...prev,
      mappings: {
        ...prev?.mappings,
        [csvField]: systemField
      }
    }));
  };

  const handleNext = () => {
    if (currentStep === 2) {
      // Validate that required fields are mapped
      const mappedFields = Object.values(importData?.mappings || {});
      const hasNameMapping = mappedFields.includes('name') || 
                             (mappedFields.includes('firstName') && mappedFields.includes('lastName'));
      
      if (!hasNameMapping) {
        alert('Please map at least one name field (Full Name, or First Name + Last Name) before proceeding.');
        return;
      }
    }
    
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleImport = async () => {
    setIsProcessing(true);
    try {
      await onImport(importData);
      handleClose();
    } catch (error) {
      console.error('Import failed:', error);
      alert('Import failed: ' + (error.message || 'Unknown error'));
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  if (mode === 'pastClients') {
    return (
      <FindPastClientsWizard
        isOpen={isOpen}
        onClose={handleClose}
        onSwitchClassic={() => setMode('classic')}
        onComplete={async (results) => {
          // Mirror classic import success messaging via parent
          await onImport({ __pastClientsResults: results });
        }}
      />
    );
  }

  if (mode === null) {
    return (
      <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-lg w-full max-w-lg overflow-hidden elevation-4">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Import Clients</h2>
              <p className="text-sm text-muted-foreground mt-1">How would you like to import?</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
          <div className="p-6 space-y-3">
            <button
              type="button"
              onClick={() => setMode('pastClients')}
              className="w-full text-left border border-primary/40 bg-primary/5 hover:bg-primary/10 rounded-lg p-4 transition-colors"
            >
              <div className="flex items-start gap-3">
                <Icon name="Sparkles" size={22} className="text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Find my past clients</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Upload any messy CRM export. AI finds people who look like past clients so you only import the right ones.
                  </p>
                  <p className="text-xs text-primary mt-2 font-medium">Recommended</p>
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMode('classic')}
              className="w-full text-left border border-border hover:bg-muted/40 rounded-lg p-4 transition-colors"
            >
              <div className="flex items-start gap-3">
                <Icon name="FileSpreadsheet" size={22} className="text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Classic CSV import</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Map columns yourself and import every row as-is.
                  </p>
                </div>
              </div>
            </button>
          </div>
          <div className="px-6 pb-6">
            <Button variant="outline" onClick={handleClose} className="w-full">Cancel</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden elevation-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Import Clients</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Step {currentStep} of 3: {
                currentStep === 1 ? 'Upload CSV File' :
                currentStep === 2 ? 'Map Fields' : 'Review & Import'
              }
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-muted/30 flex-shrink-0">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3]?.map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {step < currentStep ? <Icon name="Check" size={16} /> : step}
                </div>
                {step < 3 && (
                  <div className={`w-12 h-0.5 mx-2 ${
                    step < currentStep ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="font-medium text-foreground mb-2">Upload CSV File</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-lg mx-auto">
                  Select a CSV or Excel file containing your client data. The first row should contain column headers.
                </p>
                
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center max-w-lg mx-auto">
                  <Icon name="Upload" size={40} className="mx-auto text-primary mb-4" />
                  <p className="text-sm font-medium text-foreground">
                    {importData?.file ? importData?.file?.name : 'Choose a CSV or Excel file'}
                  </p>
                  {importData?.csvHeaders?.length > 0 && (
                    <p className="text-xs text-success mt-1">
                      ✓ Found {importData.csvHeaders.length} columns, {importData.csvData.length} rows
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2 mb-4">
                    Supported formats: CSV, Excel (.xlsx)
                  </p>
                  <label className="inline-flex">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={handleFileUpload}
                      className="sr-only"
                    />
                    <span className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground cursor-pointer hover:opacity-90 transition-opacity">
                      <Icon name="FolderOpen" size={16} />
                      {importData?.file ? 'Choose a different file' : 'Choose file'}
                    </span>
                  </label>
                </div>

                {parseError && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mt-4 max-w-lg mx-auto text-left">
                    <div className="flex items-start space-x-2">
                      <Icon name="AlertCircle" size={16} className="text-destructive mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-destructive">Error parsing CSV</p>
                        <p className="text-sm text-muted-foreground mt-1">{parseError}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-muted/30 rounded-lg p-4 max-w-lg mx-auto text-left">
                <h4 className="font-medium text-foreground mb-2">CSV Format Requirements</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• First row should contain column headers</li>
                  <li>• Use comma (,) as field separator</li>
                  <li>• Enclose text with commas in quotes</li>
                  <li>• Date format: MM/DD/YYYY</li>
                  <li>• Phone format: (555) 123-4567 or 555-123-4567</li>
                  <li>• Address is optional — use a single Address column or separate street/city/state/zip columns</li>
                </ul>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-foreground mb-2">Map CSV Fields</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Match your CSV columns to the appropriate system fields.
                </p>
              </div>

              <div className="bg-info/10 border border-info/20 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Icon name="Info" size={16} className="text-info mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Field Requirements</p>
                    <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                      <li>• <strong>Name</strong> is required for each client</li>
                      <li>• <strong>Email</strong> is optional (can be used for matching existing clients)</li>
                      <li>• <strong>Address</strong> is optional — map one full Address column, or separate street/city/state/zip columns (combined automatically)</li>
                      <li>• All other fields are optional</li>
                    </ul>
                  </div>
                </div>
              </div>

              {Object.values(importData?.mappings || {}).filter(v => v).length > 0 && (
                <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-success">
                        Auto-mapped {Object.values(importData?.mappings || {}).filter(v => v).length} field(s)
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Review and adjust the mappings below if needed.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {importData?.csvHeaders?.length > 0 ? (
                <div className="space-y-4">
                  {importData.csvHeaders.map((header, index) => {
                    const isAutoMapped = smartMatchColumn(header) === importData?.mappings?.[header];
                    return (
                      <div key={`${header}-${index}`} className="flex items-center space-x-4">
                        <div className="w-1/3 flex items-center space-x-2">
                          <label className="text-sm font-medium text-foreground">{header}</label>
                          {isAutoMapped && importData?.mappings?.[header] && (
                            <Icon name="Sparkles" size={14} className="text-success" title="Auto-matched" />
                          )}
                        </div>
                        <div className="w-2/3">
                          <Select
                            placeholder="Select system field..."
                            options={systemFields}
                            value={importData?.mappings?.[header] || ''}
                            onChange={(value) => handleMappingChange(header, value)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">
                    No CSV headers detected. Please go back and upload a valid CSV file.
                  </p>
                </div>
              )}

              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-foreground">Import Options</h4>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={importData?.options?.skipFirstRow}
                    onChange={(e) => setImportData(prev => ({
                      ...prev,
                      options: { ...prev?.options, skipFirstRow: e?.target?.checked }
                    }))}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Skip first row (headers)</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={importData?.options?.updateExisting}
                    onChange={(e) => setImportData(prev => ({
                      ...prev,
                      options: { ...prev?.options, updateExisting: e?.target?.checked }
                    }))}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Update existing clients (match by phone if email not available)</span>
                </label>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-foreground mb-2">Review Import Settings</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Please review your import settings before proceeding.
                </p>
              </div>

              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-foreground">File:</span>
                  <span className="text-sm text-muted-foreground">{importData?.file?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-foreground">Total records:</span>
                  <span className="text-sm text-muted-foreground">{importData?.csvData?.length || 0} rows</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-foreground">Columns detected:</span>
                  <span className="text-sm text-muted-foreground">{importData?.csvHeaders?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-foreground">Fields mapped:</span>
                  <span className="text-sm text-muted-foreground">
                    {Object.values(importData?.mappings || {}).filter(v => v).length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-foreground">Skip headers:</span>
                  <span className="text-sm text-muted-foreground">
                    {importData?.options?.skipFirstRow ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-foreground">Update existing:</span>
                  <span className="text-sm text-muted-foreground">
                    {importData?.options?.updateExisting ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>

              <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-warning">Important Notice</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      This import will add new clients to your database. Make sure your CSV data is accurate before proceeding.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border flex-shrink-0 bg-card">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? handleClose : handlePrevious}
            disabled={isProcessing}
          >
            {currentStep === 1 ? 'Cancel' : 'Previous'}
          </Button>

          <div className="flex items-center space-x-3">
            {currentStep < 3 ? (
              <Button
                variant="default"
                onClick={handleNext}
                disabled={currentStep === 1 && !importData?.file}
                iconName="ArrowRight"
                iconPosition="right"
              >
                Next
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={handleImport}
                loading={isProcessing}
                iconName="Upload"
                iconPosition="left"
              >
                Import Clients
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportWizard;